import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import EditPageModal from './EditPageModal';
import PageHistoryModal from './PageHistoryModal';

const PageDetailModal = ({ pageId, siteId, isOpen, onClose, onPageUpdated, onReopenModal }) => {
  const { currentLang, token, API_URL } = useAuth();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seoAnalysis, setSeoAnalysis] = useState(null);
  const [keywordAnalysis, setKeywordAnalysis] = useState(null);
  const [importantContent, setImportantContent] = useState(null);
  const { user } = useAuth();

  // Функция для детального SEO анализа HTML кода
  const analyzeHtmlSeo = (htmlCode) => {
    if (!htmlCode) return null;
    
    try {
      // Создаем временный DOM элемент для парсинга HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlCode, 'text/html');
      
      const analysis = {
        // Мета-теги
        metaTags: {
          title: doc.querySelector('title')?.textContent || null,
          description: doc.querySelector('meta[name="description"]')?.getAttribute('content') || null,
          keywords: doc.querySelector('meta[name="keywords"]')?.getAttribute('content') || null,
          robots: doc.querySelector('meta[name="robots"]')?.getAttribute('content') || null,
          viewport: doc.querySelector('meta[name="viewport"]')?.getAttribute('content') || null,
          charset: doc.querySelector('meta[charset]')?.getAttribute('charset') || doc.querySelector('meta[http-equiv="Content-Type"]')?.getAttribute('content') || null,
        },
        
        // Open Graph теги
        openGraph: {
          title: doc.querySelector('meta[property="og:title"]')?.getAttribute('content') || null,
          description: doc.querySelector('meta[property="og:description"]')?.getAttribute('content') || null,
          image: doc.querySelector('meta[property="og:image"]')?.getAttribute('content') || null,
          url: doc.querySelector('meta[property="og:url"]')?.getAttribute('content') || null,
          type: doc.querySelector('meta[property="og:type"]')?.getAttribute('content') || null,
        },
        
        // Twitter Card теги
        twitterCard: {
          card: doc.querySelector('meta[name="twitter:card"]')?.getAttribute('content') || null,
          title: doc.querySelector('meta[name="twitter:title"]')?.getAttribute('content') || null,
          description: doc.querySelector('meta[name="twitter:description"]')?.getAttribute('content') || null,
          image: doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content') || null,
        },
        
        // Заголовки
        headings: {
          h1: Array.from(doc.querySelectorAll('h1')).map(h => h.textContent.trim()).filter(Boolean),
          h2: Array.from(doc.querySelectorAll('h2')).map(h => h.textContent.trim()).filter(Boolean),
          h3: Array.from(doc.querySelectorAll('h3')).map(h => h.textContent.trim()).filter(Boolean),
          h4: Array.from(doc.querySelectorAll('h4')).map(h => h.textContent.trim()).filter(Boolean),
          h5: Array.from(doc.querySelectorAll('h5')).map(h => h.textContent.trim()).filter(Boolean),
          h6: Array.from(doc.querySelectorAll('h6')).map(h => h.textContent.trim()).filter(Boolean),
        },
        
        // Изображения
        images: {
          total: doc.querySelectorAll('img').length,
          withAlt: Array.from(doc.querySelectorAll('img')).filter(img => img.alt && img.alt.trim()).length,
          withoutAlt: Array.from(doc.querySelectorAll('img')).filter(img => !img.alt || !img.alt.trim()).length,
          list: Array.from(doc.querySelectorAll('img')).map(img => ({
            src: img.src,
            alt: img.alt || '',
            title: img.title || '',
            width: img.width || null,
            height: img.height || null,
          })),
        },
        
        // Ссылки
        links: {
          total: doc.querySelectorAll('a').length,
          internal: Array.from(doc.querySelectorAll('a')).filter(a => {
            const href = a.href;
            return href && (href.startsWith('/') || href.includes(window.location.hostname));
          }).length,
          external: Array.from(doc.querySelectorAll('a')).filter(a => {
            const href = a.href;
            return href && !href.startsWith('/') && !href.includes(window.location.hostname);
          }).length,
          nofollow: Array.from(doc.querySelectorAll('a')).filter(a => a.rel && a.rel.includes('nofollow')).length,
          list: Array.from(doc.querySelectorAll('a')).map(a => ({
            href: a.href,
            text: a.textContent.trim(),
            title: a.title || '',
            rel: a.rel || '',
            target: a.target || '',
          })),
        },
        
        // Структурированные данные
        structuredData: {
          jsonLd: Array.from(doc.querySelectorAll('script[type="application/ld+json"]')).map(script => {
            try {
              return JSON.parse(script.textContent);
            } catch {
              return null;
            }
          }).filter(Boolean),
          microdata: doc.querySelectorAll('[itemtype]').length,
        },
        
        // Формы
        forms: {
          total: doc.querySelectorAll('form').length,
          withAction: Array.from(doc.querySelectorAll('form')).filter(form => form.action).length,
          list: Array.from(doc.querySelectorAll('form')).map(form => ({
            action: form.action || '',
            method: form.method || 'GET',
            id: form.id || '',
            class: form.className || '',
          })),
        },
        
        // Скрипты и стили
        scripts: {
          total: doc.querySelectorAll('script').length,
          external: Array.from(doc.querySelectorAll('script[src]')).length,
          inline: Array.from(doc.querySelectorAll('script:not([src])')).length,
        },
        
        styles: {
          total: doc.querySelectorAll('link[rel="stylesheet"], style').length,
          external: Array.from(doc.querySelectorAll('link[rel="stylesheet"]')).length,
          inline: doc.querySelectorAll('style').length,
        },
        
        // Семантические элементы
        semantic: {
          header: doc.querySelectorAll('header').length,
          footer: doc.querySelectorAll('footer').length,
          nav: doc.querySelectorAll('nav').length,
          main: doc.querySelectorAll('main').length,
          article: doc.querySelectorAll('article').length,
          section: doc.querySelectorAll('section').length,
          aside: doc.querySelectorAll('aside').length,
        },
        
        // Производительность
        performance: {
          totalElements: doc.querySelectorAll('*').length,
          textNodes: Array.from(doc.querySelectorAll('*')).filter(el => el.childNodes.length === 1 && el.childNodes[0].nodeType === 3).length,
        },
        
        // Контент анализ
        content: {
          // Извлекаем весь текстовый контент, исключая скрипты
          fullText: (() => {
            if (!doc.body) return '';
            // Создаем копию body для безопасного удаления скриптов
            const bodyClone = doc.body.cloneNode(true);
            // Удаляем все script и style элементы
            bodyClone.querySelectorAll('script, style, noscript').forEach(el => el.remove());
            // Извлекаем текст с сохранением структуры
            const extractText = (element) => {
              let text = '';
              for (let node of element.childNodes) {
                if (node.nodeType === Node.TEXT_NODE) {
                  text += node.textContent;
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                  // Добавляем перенос строки для блочных элементов
                  if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'br'].includes(node.tagName.toLowerCase())) {
                    text += extractText(node) + ' | ';
                  } else {
                    text += extractText(node);
                  }
                }
              }
              text = text.replace(/ \|  \| /g, ' | ');
              return text;
            };
            return extractText(bodyClone).trim();
          })(),
          // Подсчитываем слова
          wordCount: (() => {
            if (!doc.body) return 0;
            const bodyClone = doc.body.cloneNode(true);
            bodyClone.querySelectorAll('script, style, noscript').forEach(el => el.remove());
            return (bodyClone.textContent || '').split(/\s+/).filter(word => word.length > 0).length;
          })(),
          // Подсчитываем символы
          charCount: (() => {
            if (!doc.body) return 0;
            const bodyClone = doc.body.cloneNode(true);
            bodyClone.querySelectorAll('script, style, noscript').forEach(el => el.remove());
            return (bodyClone.textContent || '').length;
          })(),
          // Подсчитываем предложения
          sentenceCount: (() => {
            if (!doc.body) return 0;
            const bodyClone = doc.body.cloneNode(true);
            bodyClone.querySelectorAll('script, style, noscript').forEach(el => el.remove());
            return (bodyClone.textContent || '').split(/[.!?]+/).filter(sentence => sentence.trim().length > 0).length;
          })(),
          // Подсчитываем абзацы
          paragraphCount: doc.querySelectorAll('p').length,
        },
        
        // Каноникал
        canonical: {
          href: doc.querySelector('link[rel="canonical"]')?.getAttribute('href') || null,
          selfReferencing: (() => {
            const canonical = doc.querySelector('link[rel="canonical"]')?.getAttribute('href');
            if (!canonical) return null;
            try {
              const canonicalUrl = new URL(canonical, window.location.origin);
              const currentUrl = new URL(window.location.href);
              return canonicalUrl.pathname === currentUrl.pathname;
            } catch {
              return null;
            }
          })(),
        },
        
        // Дополнительные мета-теги
        additionalMeta: {
          author: doc.querySelector('meta[name="author"]')?.getAttribute('content') || null,
          generator: doc.querySelector('meta[name="generator"]')?.getAttribute('content') || null,
          language: doc.querySelector('html')?.getAttribute('lang') || null,
          themeColor: doc.querySelector('meta[name="theme-color"]')?.getAttribute('content') || null,
          msapplicationTileColor: doc.querySelector('meta[name="msapplication-TileColor"]')?.getAttribute('content') || null,
        },
      };
 
      // Анализируем тошноту текста
      if (analysis.content.fullText) {
        analysis.content.textAnalysis = analyzeTextDensity(analysis.content.fullText);
      }
      
      return analysis;
    } catch (error) {
      return null;
    }
  };

  // Функция для копирования в буфер обмена
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Можно добавить уведомление об успешном копировании
    }).catch(err => {
    });
  };

  // Функция для анализа тошноты текста
  const analyzeTextDensity = (text) => {
    if (!text) return null;
    
    // Очищаем текст от лишних символов
    const cleanText = text.toLowerCase()
      .replace(/[^\w\sа-яё]/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Разбиваем на слова
    const words = cleanText.split(/\s+/).filter(word => word.length > 2);
    
    // Подсчитываем частоту слов
    const wordFrequency = {};
    words.forEach(word => {
      wordFrequency[word] = (wordFrequency[word] || 0) + 1;
    });
    
    // Сортируем по частоте
    const sortedWords = Object.entries(wordFrequency)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 20); // Топ 20 слов
    
    // Вычисляем тошноту (процент самого частого слова)
    const totalWords = words.length;
    const mostFrequentWord = sortedWords[0];
    const density = mostFrequentWord ? (mostFrequentWord[1] / totalWords * 100) : 0;
    
    return {
      totalWords,
      uniqueWords: Object.keys(wordFrequency).length,
      mostFrequentWords: sortedWords,
      density: density.toFixed(2),
      densityLevel: density > 5 ? 'high' : density > 3 ? 'medium' : 'low',
    };
  };

  // Функция для извлечения важного контента
  const extractImportantContent = (text) => {
    if (!text) return null;
    
    // Очищаем текст от лишних символов
    const cleanText = text
      .replace(/\s+/g, ' ')
      .trim();
    
    // Разбиваем на предложения с более точным разделением
    const sentences = cleanText
      .split(/[|]+/)
      .map(sentence => sentence.trim())
      .filter(sentence => sentence.length > 100 && sentence.length < 10000)
      .filter(sentence => {
        // Исключаем предложения с техническими элементами и заголовками
        const techPatterns = [
          /^cookie/i, /^privacy/i, /^terms/i, /^policy/i, /^©/i, /^all rights reserved/i,
          /^subscribe/i, /^newsletter/i, /^follow us/i, /^social media/i,
          /^menu$/i, /^navigation$/i, /^footer$/i, /^header$/i, /^sidebar$/i,
          /^click here$/i, /^read more$/i, /^learn more$/i, /^contact us$/i,
          /^phone$/i, /^email$/i, /^address$/i, /^fax$/i, /^tel:/i, /^mailto:/i,
          /^javascript$/i, /^function$/i, /^var$/i, /^console$/i, /^error$/i,
          /^loading$/i, /^please wait$/i, /^processing$/i, /^submitting$/i,
          /^back to top$/i, /^scroll to top$/i, /^go to top$/i,
          /^accept$/i, /^decline$/i, /^agree$/i, /^disagree$/i,
          /^sign up$/i, /^log in$/i, /^register$/i, /^login$/i,
          /^search$/i, /^find$/i, /^look for$/i, /^browse$/i,
          // Добавляем паттерны для заголовков
          /^h1\./i, /^h2\./i, /^h3\./i, /^h4\./i, /^h5\./i, /^h6\./i,
          /<script/i, /<\/script>/i, /^script$/i,
          /<h[1-6]/i, /<\/h[1-6]>/i,
          /^title:/i, /^heading:/i
        ];
        
        // Проверяем только точные совпадения в начале предложения
        return !techPatterns.some(pattern => pattern.test(sentence));
      })
      .filter(sentence => {
        // Более мягкая фильтрация по символам
        const digitCount = (sentence.match(/\d/g) || []).length;
        const specialCharCount = (sentence.match(/[^\w\sа-яё]/gi) || []).length;
        const wordCount = sentence.split(/\s+/).filter(word => word.length > 0).length;
        
        // Если больше 50% символов - цифры или спецсимволы, исключаем
        return digitCount / sentence.length < 0.5 && 
               specialCharCount / sentence.length < 0.5 &&
               wordCount >= 2;
      })
      .filter(sentence => {
        // Дополнительная фильтрация - исключаем предложения, которые состоят только из одного слова
        const words = sentence.split(/\s+/).filter(word => word.length > 0);
        return words.length > 1;
      })
      .slice(0, 100);
    
    return sentences;
  };

  // Функция для выделения ключевых слов в тексте
  const highlightKeywords = (text, keywords) => {
    if (!text || !keywords || keywords.length === 0) return text;
    
    let highlightedText = text;
    
    keywords.forEach(keyword => {
      const cleanKeyword = keyword.toLowerCase();
      const regex = new RegExp(`(${cleanKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
    });
    
    return highlightedText;
  };

  // Функция для анализа вхождения ключевых запросов
  const analyzeKeywordPresence = (text, keywords) => {
    if (!text || !keywords || keywords.length === 0) return null;
    
    const cleanText = text.toLowerCase();
    const results = [];
    
    keywords.forEach(keyword => {
      const cleanKeyword = keyword.toLowerCase();
      const keywordWords = cleanKeyword.split(/\s+/).filter(word => word.length > 0);
      
      // Проверяем точное вхождение фразы
      const exactMatches = (cleanText.match(new RegExp(cleanKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
      
      // Проверяем вхождение всех слов из ключевой фразы
      const allWordsPresent = keywordWords.every(word => 
        cleanText.includes(word)
      );
      
      // Подсчитываем вхождения отдельных слов
      const wordMatches = {};
      keywordWords.forEach(word => {
        const matches = (cleanText.match(new RegExp(word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
        wordMatches[word] = matches;
      });
      
      // Определяем статус
      let status = 'missing';
      if (exactMatches > 0) {
        status = 'exact';
      } else if (allWordsPresent) {
        status = 'partial';
      } else if (Object.values(wordMatches).some(count => count > 0)) {
        status = 'words';
      }
      
      results.push({
        keyword,
        exactMatches,
        allWordsPresent,
        wordMatches,
        status,
        // Дополнительная информация для анализа
        inTitle: seoAnalysis?.metaTags.title?.toLowerCase().includes(cleanKeyword) || false,
        inDescription: seoAnalysis?.metaTags.description?.toLowerCase().includes(cleanKeyword) || false,
        inH1: seoAnalysis?.headings.h1.some(h1 => h1.toLowerCase().includes(cleanKeyword)) || false,
      });
    });
    
    return results;
  };

  // Загружаем данные страницы при открытии модалки
  useEffect(() => {
    if (isOpen && pageId && siteId) {
      fetchPageData();
    }
  }, [isOpen, pageId, siteId]);

  // Управляем прокруткой body при открытии/закрытии модалки
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchPageData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}/pages/${pageId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.page) {
        setPage(data.page);
        // Анализируем HTML код если он есть
        if (data.page.html_code) {
          const analysis = analyzeHtmlSeo(data.page.html_code);
          setSeoAnalysis(analysis);
          
          // Анализируем вхождение ключевых запросов
          const parsedKeywords = parseKeywords(data.page.key);
          if (parsedKeywords.length > 0 && analysis?.content?.fullText) {
            const keywordResults = analyzeKeywordPresence(analysis.content.fullText, parsedKeywords);
            setKeywordAnalysis(keywordResults);
          }
          
          // Извлекаем важный контент
          if (analysis?.content?.fullText) {
            const content = extractImportantContent(analysis.content.fullText);
            setImportantContent(content);
          }
        }
      } else {
        throw new Error(data.message || 'Ошибка загрузки данных страницы');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const decodeBase64 = (str) => {
    if (!str) return '';
    
    // Проверяем, является ли строка Base64
    const isBase64 = /^[A-Za-z0-9+/]*={0,2}$/.test(str) && str.length % 4 === 0;
    
    if (isBase64) {
      try {
        const decoded = atob(str);
        // Декодируем UTF-8 если данные пришли в неправильной кодировке
        try {
          return decodeURIComponent(escape(decoded));
        } catch {
          // Если не удалось декодировать как UTF-8, возвращаем как есть
          return decoded;
        }
      } catch {
        // Если не удалось декодировать Base64, возвращаем исходную строку
        return str;
      }
    } else {
      // Если это не Base64, возвращаем строку как есть
      return str;
    }
  };

  const parseKeywords = (keyData) => {
    if (!keyData) return [];
    
    try {
      const decoded = decodeBase64(keyData);
      const keywords = [];
      
      const matches = decoded.match(/"([^"]+)"/g);
      if (matches) {
        const extractedKeywords = matches.map(match => match.replace(/"/g, ''));
        keywords.push(...extractedKeywords.map(keyword => {
          try {
            return decodeURIComponent(escape(keyword));
          } catch {
            return keyword;
          }
        }));
      }
      
      if (keywords.length === 0) {
        const altMatches = decoded.match(/s:\d+:"([^"]+)"/g);
        if (altMatches) {
          const altKeywords = altMatches.map(match => {
            const content = match.match(/s:\d+:"([^"]+)"/);
            return content ? content[1] : '';
          }).filter(Boolean);
          
          keywords.push(...altKeywords.map(keyword => {
            try {
              return decodeURIComponent(escape(keyword));
            } catch {
              return keyword;
            }
          }));
        }
      }
      
      return keywords;
    } catch (error) {
      return [];
    }
  };

  if (!isOpen) return null;

  // Показываем состояние загрузки
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">
                {currentLang === 'ru' ? 'Загрузка данных страницы...' : 'Loading page data...'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Показываем ошибку
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">
                {currentLang === 'ru' ? 'Ошибка загрузки' : 'Loading Error'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-red-50 p-4 border-l-4 border-red-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {currentLang === 'ru' ? 'Ошибка загрузки данных' : 'Error loading data'}
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {error}
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={fetchPageData}
                      className="bg-red-100 text-red-800 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      {currentLang === 'ru' ? 'Попробовать снова' : 'Try again'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Если нет данных страницы
  if (!page) {
    return null;
  }

  const parseHeaders = (textH) => {
    if (!textH) return {};
    
    try {
      const decoded = decodeBase64(textH);
      const headers = {};
      
      const h1Regex = /h1.*?text.*?{([^}]*(?:{[^}]*}[^}]*)*)}/g;
      const h2Regex = /h2.*?text.*?{([^}]*(?:{[^}]*}[^}]*)*)}/g;
      const h3Regex = /h3.*?text.*?{([^}]*(?:{[^}]*}[^}]*)*)}/g;
      
      const h1Matches = [...decoded.matchAll(h1Regex)];
      if (h1Matches.length > 0) {
        const h1Content = h1Matches[0][1];
        const h1Items = h1Content.match(/"([^"]+)"/g)?.map(match => match.replace(/"/g, '')) || [];
        headers.h1 = h1Items.map(item => {
          try {
            return decodeURIComponent(escape(item));
          } catch {
            return item;
          }
        });
      }
      
      const h2Matches = [...decoded.matchAll(h2Regex)];
      if (h2Matches.length > 0) {
        const h2Content = h2Matches[0][1];
        const h2Items = h2Content.match(/"([^"]+)"/g)?.map(match => match.replace(/"/g, '')) || [];
        headers.h2 = h2Items.map(item => {
          try {
            return decodeURIComponent(escape(item));
          } catch {
            return item;
          }
        });
      }
      
      const h3Matches = [...decoded.matchAll(h3Regex)];
      if (h3Matches.length > 0) {
        const h3Content = h3Matches[0][1];
        const h3Items = h3Content.match(/"([^"]+)"/g)?.map(match => match.replace(/"/g, '')) || [];
        headers.h3 = h3Items.map(item => {
          try {
            return decodeURIComponent(escape(item));
          } catch {
            return item;
          }
        });
      }
      
      return headers;
    } catch (error) {
      return {};
    }
  };

  const parseTextTd = (textTd) => {
    if (!textTd) return null;
    
    try {
      const decoded = decodeBase64(textTd);
      
      // Парсим PHP serialize формат
      const result = {};
      
      // Извлекаем size
      const sizeMatch = decoded.match(/size";d:([^;]+)/);
      if (sizeMatch) {
        result.size = parseFloat(sizeMatch[1]);
      }
      
      // Извлекаем down_time
      const downTimeMatch = decoded.match(/down_time";d:([^;]+)/);
      if (downTimeMatch) {
        result.down_time = parseFloat(downTimeMatch[1]);
      }
      
      // Извлекаем speed
      const speedMatch = decoded.match(/speed";d:([^;]+)/);
      if (speedMatch) {
        result.speed = parseFloat(speedMatch[1]);
      }
      
      // Извлекаем text (строка, а не массив)
      const textMatch = decoded.match(/text";s:\d+:"([^"]+)"/);
      if (textMatch) {
        try {
          const decodedText = decodeURIComponent(escape(textMatch[1]));
          result.text = decodedText;
        } catch {
          result.text = textMatch[1];
        }
      }
      
      // Извлекаем headers
      const headersMatch = decoded.match(/headers";a:\d+:({[^}]*})/);
      if (headersMatch) {
        const headersContent = headersMatch[1];
        const headers = {};
        
        // Извлекаем server
        const serverMatch = headersContent.match(/server";s:\d+:"([^"]+)"/);
        if (serverMatch) {
          headers.server = serverMatch[1];
        }
        
        // Извлекаем date
        const dateMatch = headersContent.match(/date";s:\d+:"([^"]+)"/);
        if (dateMatch) {
          headers.date = dateMatch[1];
        }
        
        // Извлекаем content-type
        const contentTypeMatch = headersContent.match(/content-type";s:\d+:"([^"]+)"/);
        if (contentTypeMatch) {
          headers['content-type'] = contentTypeMatch[1];
        }
        
        // Извлекаем x-powered-by
        const poweredByMatch = headersContent.match(/x-powered-by";s:\d+:"([^"]+)"/);
        if (poweredByMatch) {
          headers['x-powered-by'] = poweredByMatch[1];
        }
        
        result.headers = headers;
      }
      
      return result;
    } catch (error) {
      return null;
    }
  };

  const parseLinks = (linkData) => {
    if (!linkData) return [];
    
    try {
      // Если данные уже в JSON формате (массив объектов)
      if (Array.isArray(linkData)) {
        return linkData.map(link => {
          return {
            original: link,
            type: link.type || (link.nofollow ? 'NoFollow' : 'Follow'),
            url: link.url || '',
            anchor: link.text || link.anchor || link.url || ''
          };
        });
      }
      
      // Если это строка, пытаемся её распарсить
      if (typeof linkData === 'string') {
        // Сначала пробуем как JSON
        try {
          const parsed = JSON.parse(linkData);
          if (Array.isArray(parsed)) {
            return parsed.map(link => {
              return {
                original: link,
                type: link.type || (link.nofollow ? 'NoFollow' : 'Follow'),
                url: link.url || '',
                anchor: link.text || link.anchor || link.url || ''
              };
            });
          }
        } catch (e) {
          // Если не JSON, пробуем как PHP serialize
          try {
            // Проверяем, начинается ли строка с "a:" (массив в PHP serialize)
            if (linkData.startsWith('a:')) {
              const links = [];
              
              // Парсим PHP serialize формат для массива объектов
              // Ищем все пары s:3:"url" и s:4:"text" (или s:X:"text")
              const urlMatches = linkData.match(/s:\d+:"url";s:\d+:"([^"]+)"/g);
              const textMatches = linkData.match(/s:\d+:"text";s:\d+:"([^"]+)"/g);
              
              if (urlMatches && textMatches && urlMatches.length === textMatches.length) {
                for (let i = 0; i < urlMatches.length; i++) {
                  const urlMatch = urlMatches[i].match(/s:\d+:"url";s:\d+:"([^"]+)"/);
                  const textMatch = textMatches[i].match(/s:\d+:"text";s:\d+:"([^"]+)"/);
                  
                  if (urlMatch && textMatch) {
                    links.push({
                      original: { url: urlMatch[1], text: textMatch[1] },
                      type: 'Follow', // По умолчанию Follow, можно добавить логику для определения NoFollow
                      url: urlMatch[1],
                      anchor: textMatch[1]
                    });
                  }
                }
              }
              
              return links;
            }
            
            // Если не PHP serialize, пробуем как Base64 + PHP serialize
            try {
              const decoded = decodeBase64(linkData);
              return parseLinks(decoded); // Рекурсивно парсим декодированные данные
            } catch (decodeError) {
              return [];
            }
          } catch (serializeError) {
            return [];
          }
        }
      }
      
      return [];
    } catch (error) {
      return [];
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return currentLang === 'ru' ? 'Не указана' : 'Not specified';
    return new Date(timestamp * 1000).toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US');
  };

  const headers = parseHeaders(page.text_h);
  const keywords = parseKeywords(page.key);
  const textTdData = parseTextTd(page.text_td);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl mx-auto max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800">
              {currentLang === 'ru' ? 'Детали страницы' : 'Page Details'}
            </h3>
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              {user?.role === 'admin' && (
              <button
                onClick={() => setEditModalOpen(true)}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base"
              >
                {currentLang === 'ru' ? 'Редактировать' : 'Edit'}
              </button>
              )}
              <button
                onClick={() => setHistoryModalOpen(true)}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm sm:text-base"
              >
                {currentLang === 'ru' ? 'История' : 'History'}
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {page.text && (
            <div className="bg-gray-50 p-3 sm:p-4 border-l-4 border-red-500 italic text-sm text-red-500 mb-4">
              {page.text}
            </div>
          )}
          <div className="space-y-4 sm:space-y-6">

            {/* Ключевые запросы */}
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">
                {currentLang === 'ru' ? 'Ключевые запросы:' : 'Key Queries:'} ({keywords.length})
              </h4>
              <div className="bg-gray-50 p-3 rounded-lg">
                {keywords.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {keywords.map((keyword, index) => (
                      <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white px-3 py-2 rounded-lg border gap-2 sm:gap-0">
                        <span className="text-sm text-gray-800 break-words flex-1">{keyword}</span>
                        <div className="flex items-center space-x-2 w-full sm:w-auto">
                          <a
                            href={`https://www.google.com/search?q=${encodeURIComponent(keyword)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center space-x-1 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors flex-1 sm:flex-none"
                            title={`${currentLang === 'ru' ? 'Поиск в Google' : 'Search in Google'}: ${keyword}`}
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            <span className="text-xs">Google</span>
                          </a>
                          <a
                            href={`https://yandex.ru/search/?text=${encodeURIComponent(keyword)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center space-x-1 px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors flex-1 sm:flex-none"
                            title={`${currentLang === 'ru' ? 'Поиск в Яндекс' : 'Search in Yandex'}: ${keyword}`}
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M13.4 2.02C13.4.91 12.49 0 11.38 0s-2.02.91-2.02 2.02v6.01c0 1.11.91 2.02 2.02 2.02s2.02-.91 2.02-2.02V2.02z"/>
                              <path d="M17.42 4.08c-.55 0-1 .45-1 1v4.95c0 .55.45 1 1 1s1-.45 1-1V5.08c0-.55-.45-1-1-1z"/>
                              <path d="M7.42 4.08c-.55 0-1 .45-1 1v4.95c0 .55.45 1 1 1s1-.45 1-1V5.08c0-.55-.45-1-1-1z"/>
                              <path d="M21.44 6.1c-.55 0-1 .45-1 1v2.93c0 .55.45 1 1 1s1-.45 1-1V7.1c0-.55-.45-1-1-1z"/>
                              <path d="M3.44 6.1c-.55 0-1 .45-1 1v2.93c0 .55.45 1 1 1s1-.45 1-1V7.1c0-.55-.45-1-1-1z"/>
                            </svg>
                            <span className="text-xs">Яндекс</span>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">
                    {currentLang === 'ru' ? 'Ключевые запросы не найдены' : 'No key queries found'}
                  </span>
                )}
              </div>
            </div>

            {/* Анализ вхождения ключевых запросов */}
            {keywordAnalysis && keywordAnalysis.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-600 mb-2">
                  {currentLang === 'ru' ? 'Анализ вхождения ключевых запросов:' : 'Keyword Presence Analysis:'}
                </h4>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="space-y-3">
                    {keywordAnalysis.map((result, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium text-gray-800 text-sm">
                            {result.keyword}
                          </h5>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            result.status === 'exact' ? 'bg-green-100 text-green-800' :
                            result.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                            result.status === 'words' ? 'bg-blue-100 text-blue-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {result.status === 'exact' ? (currentLang === 'ru' ? 'Точное вхождение' : 'Exact match') :
                             result.status === 'partial' ? (currentLang === 'ru' ? 'Частичное' : 'Partial') :
                             result.status === 'words' ? (currentLang === 'ru' ? 'Отдельные слова' : 'Words only') :
                             (currentLang === 'ru' ? 'Отсутствует' : 'Missing')}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                          <div>
                            <div className="font-medium text-gray-700 mb-2">
                              {currentLang === 'ru' ? 'Статистика:' : 'Statistics:'}
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  {currentLang === 'ru' ? 'Точных вхождений:' : 'Exact matches:'}
                                </span>
                                <span className="font-medium">{result.exactMatches}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  {currentLang === 'ru' ? 'Все слова есть:' : 'All words present:'}
                                </span>
                                <span className={`font-medium ${result.allWordsPresent ? 'text-green-600' : 'text-red-600'}`}>
                                  {result.allWordsPresent ? (currentLang === 'ru' ? 'Да' : 'Yes') : (currentLang === 'ru' ? 'Нет' : 'No')}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <div className="font-medium text-gray-700 mb-2">
                              {currentLang === 'ru' ? 'Вхождение в мета-тегах:' : 'Meta tags presence:'}
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Title:</span>
                                <span className={`font-medium ${result.inTitle ? 'text-green-600' : 'text-red-600'}`}>
                                  {result.inTitle ? (currentLang === 'ru' ? 'Есть' : 'Yes') : (currentLang === 'ru' ? 'Нет' : 'No')}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Description:</span>
                                <span className={`font-medium ${result.inDescription ? 'text-green-600' : 'text-red-600'}`}>
                                  {result.inDescription ? (currentLang === 'ru' ? 'Есть' : 'Yes') : (currentLang === 'ru' ? 'Нет' : 'No')}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">H1:</span>
                                <span className={`font-medium ${result.inH1 ? 'text-green-600' : 'text-red-600'}`}>
                                  {result.inH1 ? (currentLang === 'ru' ? 'Есть' : 'Yes') : (currentLang === 'ru' ? 'Нет' : 'No')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Детали по словам */}
                        {Object.keys(result.wordMatches).length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="font-medium text-gray-700 mb-2 text-xs">
                              {currentLang === 'ru' ? 'Вхождения отдельных слов:' : 'Individual word matches:'}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(result.wordMatches).map(([word, count]) => (
                                <div key={word} className={`px-2 py-1 rounded text-xs ${
                                  count > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  <span className="font-medium">{word}:</span> {count}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
                         )}

             

             {/* Детальный SEO анализ */}
            {seoAnalysis && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 sm:p-6 rounded-lg border border-green-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  {currentLang === 'ru' ? 'Детальный SEO Анализ' : 'Detailed SEO Analysis'}
                </h4>

                                 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                   {/* Мета-теги */}
                   <div className="space-y-4">
                     <h5 className="font-semibold text-gray-700 border-b pb-2">
                       {currentLang === 'ru' ? 'Мета-теги' : 'Meta Tags'}
                     </h5>
                     
                     <div className="space-y-3">
                       <div>
                         <span className="text-sm font-medium text-gray-600">
                           {currentLang === 'ru' ? 'Title:' : 'Title:'}
                         </span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.metaTags.title || (
                             <span className="text-red-500 italic">
                               {currentLang === 'ru' ? 'Не найден' : 'Not found'}
                             </span>
                           )}
                         </div>
                       </div>

                       <div>
                         <span className="text-sm font-medium text-gray-600">
                           {currentLang === 'ru' ? 'Description:' : 'Description:'}
                         </span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.metaTags.description || (
                             <span className="text-red-500 italic">
                               {currentLang === 'ru' ? 'Не найден' : 'Not found'}
                             </span>
                           )}
                         </div>
                       </div>

                       <div>
                         <span className="text-sm font-medium text-gray-600">
                           {currentLang === 'ru' ? 'Keywords:' : 'Keywords:'}
                         </span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.metaTags.keywords || (
                             <span className="text-red-500 italic">
                               {currentLang === 'ru' ? 'Не найдены' : 'Not found'}
                             </span>
                           )}
                         </div>
                       </div>

                       <div>
                         <span className="text-sm font-medium text-gray-600">
                           {currentLang === 'ru' ? 'Robots:' : 'Robots:'}
                         </span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.metaTags.robots || (
                             <span className="text-gray-500 italic">
                               {currentLang === 'ru' ? 'Не указан' : 'Not specified'}
                             </span>
                           )}
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Open Graph */}
                   <div className="space-y-4">
                     <h5 className="font-semibold text-gray-700 border-b pb-2">
                       {currentLang === 'ru' ? 'Open Graph' : 'Open Graph'}
                     </h5>
                     
                     <div className="space-y-3">
                       <div>
                         <span className="text-sm font-medium text-gray-600">og:title:</span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.openGraph.title || (
                             <span className="text-red-500 italic">
                               {currentLang === 'ru' ? 'Не найден' : 'Not found'}
                             </span>
                           )}
                         </div>
                       </div>

                       <div>
                         <span className="text-sm font-medium text-gray-600">og:description:</span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.openGraph.description || (
                             <span className="text-red-500 italic">
                               {currentLang === 'ru' ? 'Не найден' : 'Not found'}
                             </span>
                           )}
                         </div>
                       </div>

                       <div>
                         <span className="text-sm font-medium text-gray-600">og:image:</span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.openGraph.image || (
                             <span className="text-red-500 italic">
                               {currentLang === 'ru' ? 'Не найден' : 'Not found'}
                             </span>
                           )}
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Каноникал и дополнительные мета-теги */}
                   <div className="space-y-4">
                     <h5 className="font-semibold text-gray-700 border-b pb-2">
                       {currentLang === 'ru' ? 'Дополнительно' : 'Additional'}
                     </h5>
                     
                     <div className="space-y-3">
                       <div>
                         <span className="text-sm font-medium text-gray-600">
                           {currentLang === 'ru' ? 'Canonical:' : 'Canonical:'}
                         </span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.canonical.href || (
                             <span className="text-red-500 italic">
                               {currentLang === 'ru' ? 'Не указан' : 'Not specified'}
                             </span>
                           )}
                         </div>
                       </div>

                       <div>
                         <span className="text-sm font-medium text-gray-600">
                           {currentLang === 'ru' ? 'Язык:' : 'Language:'}
                         </span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.additionalMeta.language || (
                             <span className="text-red-500 italic">
                               {currentLang === 'ru' ? 'Не указан' : 'Not specified'}
                             </span>
                           )}
                         </div>
                       </div>

                       <div>
                         <span className="text-sm font-medium text-gray-600">
                           {currentLang === 'ru' ? 'Автор:' : 'Author:'}
                         </span>
                         <div className="text-sm bg-white p-2 rounded border">
                           {seoAnalysis.additionalMeta.author || (
                             <span className="text-gray-500 italic">
                               {currentLang === 'ru' ? 'Не указан' : 'Not specified'}
                             </span>
                           )}
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>

                 <div className="mt-3">
                    <div className="text-sm font-medium text-gray-600">
                      {currentLang === 'ru' ? 'H1:' : 'H1:'}
                    </div>
                    <div className="text-sm bg-white p-2 rounded border">
                      {headers.h1 && headers.h1.length > 0 ? (
                        <div className="space-y-1">
                          {headers.h1.map((h1, index) => (
                            <div key={index} className="text-sm text-gray-800 break-words">
                              {h1}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">
                          {currentLang === 'ru' ? 'Не найдено' : 'Not found'}
                        </span>
                      )}
                    </div>
                  </div>
                                 {/* Статистика */}
                 <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                   <div className="text-center bg-white p-3 rounded-lg border">
                     <div className="text-lg font-bold text-blue-600">{seoAnalysis.headings.h1.length}</div>
                     <div className="text-xs text-gray-600">H1</div>
                   </div>
                   <div className="text-center bg-white p-3 rounded-lg border">
                     <div className="text-lg font-bold text-blue-600">{seoAnalysis.headings.h2.length}</div>
                     <div className="text-xs text-gray-600">H2</div>
                   </div>
                   <div className="text-center bg-white p-3 rounded-lg border">
                     <div className="text-lg font-bold text-blue-600">{seoAnalysis.headings.h3.length}</div>
                     <div className="text-xs text-gray-600">H3</div>
                   </div>
                   <div className="text-center bg-white p-3 rounded-lg border">
                     <div className="text-lg font-bold text-green-600">{seoAnalysis.images.total}</div>
                     <div className="text-xs text-gray-600">
                       {currentLang === 'ru' ? 'Img' : 'Img'}
                     </div>
                   </div>
                   <div className="text-center bg-white p-3 rounded-lg border">
                     <div className="text-lg font-bold text-purple-600">{seoAnalysis.links.total}</div>
                     <div className="text-xs text-gray-600">
                       {currentLang === 'ru' ? 'Ссылки' : 'Links'}
                     </div>
                   </div>
                   <div className="text-center bg-white p-3 rounded-lg border">
                     <div className="text-lg font-bold text-orange-600">{seoAnalysis.forms.total}</div>
                     <div className="text-xs text-gray-600">
                       {currentLang === 'ru' ? 'Формы' : 'Forms'}
                     </div>
                   </div>
                   <div className="text-center bg-white p-3 rounded-lg border">
                     <div className="text-lg font-bold text-indigo-600">{seoAnalysis.content.wordCount}</div>
                     <div className="text-xs text-gray-600">
                       {currentLang === 'ru' ? 'Слова' : 'Words'}
                     </div>
                   </div>
                   <div className="text-center bg-white p-3 rounded-lg border">
                     <div className="text-lg font-bold text-teal-600">{seoAnalysis.content.paragraphCount}</div>
                     <div className="text-xs text-gray-600">
                       {currentLang === 'ru' ? 'Абзацы' : 'Paragraphs'}
                     </div>
                   </div>
                 </div>
                  
                 {/* Анализ контента */}
                 {seoAnalysis.content.textAnalysis && (
                   <div className="mt-6 bg-white p-4 rounded-lg border">
                     <h5 className="font-semibold text-gray-700 mb-3">
                       {currentLang === 'ru' ? 'Анализ контента' : 'Content Analysis'}
                     </h5>
                     
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                       <div className="text-center">
                         <div className="text-2xl font-bold text-indigo-600">{seoAnalysis.content.textAnalysis.totalWords}</div>
                         <div className="text-xs text-gray-600">
                           {currentLang === 'ru' ? 'Всего слов' : 'Total Words'}
                         </div>
                       </div>
                       <div className="text-center">
                         <div className="text-2xl font-bold text-green-600">{seoAnalysis.content.textAnalysis.uniqueWords}</div>
                         <div className="text-xs text-gray-600">
                           {currentLang === 'ru' ? 'Уникальных слов' : 'Unique Words'}
                         </div>
                       </div>
                       <div className="text-center">
                         <div className={`text-2xl font-bold ${
                           seoAnalysis.content.textAnalysis.densityLevel === 'high' ? 'text-red-600' :
                           seoAnalysis.content.textAnalysis.densityLevel === 'medium' ? 'text-yellow-600' : 'text-green-600'
                         }`}>
                           {seoAnalysis.content.textAnalysis.density}%
                         </div>
                         <div className="text-xs text-gray-600">
                           {currentLang === 'ru' ? 'Тошнота текста' : 'Text Density'}
                         </div>
                       </div>
                       <div className="text-center">
                         <div className="text-2xl font-bold text-blue-600">{seoAnalysis.content.sentenceCount}</div>
                         <div className="text-xs text-gray-600">
                           {currentLang === 'ru' ? 'Предложений' : 'Sentences'}
                         </div>
                       </div>
                     </div>

                     {/* Топ слов */}
                     {seoAnalysis.content.textAnalysis.mostFrequentWords.length > 0 && (
                       <div className="mt-4">
                         <h6 className="text-sm font-medium text-gray-700 mb-2">
                           {currentLang === 'ru' ? 'Топ-10 самых частых слов:' : 'Top-10 most frequent words:'}
                         </h6>
                         <div className="flex flex-wrap gap-2">
                           {seoAnalysis.content.textAnalysis.mostFrequentWords.slice(0, 10).map(([word, count], index) => (
                             <div key={index} className="bg-gray-100 px-2 py-1 rounded text-xs">
                               <span className="font-medium">{word}</span>
                               <span className="text-gray-500 ml-1">({count})</span>
                             </div>
                           ))}
                         </div>
                       </div>
                     )}
                   </div>
                 )}
                
                 {/* Важный контент страницы */}
                 {importantContent && importantContent.length > 0 && (
                   <div>
                      <h5 className="font-semibold text-gray-700 border-b pb-2 pt-5 mb-2">
                       {currentLang === 'ru' ? 'Важный контент страницы' : 'Important Page Content'}
                      </h5>

                     <div className="bg-gray-50 p-3 rounded-lg">
                       <div className="space-y-3">
                         {importantContent.map((sentence, index) => (
                           <div key={index} className="bg-white p-3 rounded-lg border border-gray-200">
                             <div className="flex items-start gap-3">
                               <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                                 {index + 1}
                               </div>
                               <div className="flex-1">
                                 <p 
                                   className="text-sm text-gray-800 leading-relaxed"
                                   dangerouslySetInnerHTML={{
                                     __html: highlightKeywords(sentence, keywords)
                                   }}
                                 />
                                 <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                                   <span>{sentence.length} символов</span>
                                   <span>•</span>
                                   <span>{sentence.split(' ').length} слов</span>
                                 </div>
                               </div>
                               <div className="flex-shrink-0">
                                 <button
                                   onClick={() => copyToClipboard(sentence)}
                                   className="text-gray-400 hover:text-gray-600 p-1"
                                   title={currentLang === 'ru' ? 'Копировать предложение' : 'Copy sentence'}
                                 >
                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                   </svg>
                                 </button>
                               </div>
                             </div>
                           </div>
                         ))}
                       </div>
                       <div className="mt-3 pt-3 border-t border-gray-200">
                         <div className="text-xs text-gray-600">
                           <strong>{currentLang === 'ru' ? 'Примечание:' : 'Note:'}</strong> {currentLang === 'ru' ? 'Показаны основные предложения страницы, исключая технические элементы и навигацию' : 'Showing main page sentences, excluding technical elements and navigation'}
                         </div>
                       </div>
                     </div>
                   </div>
                 )}

                {/* Проблемы */}
                 <div className="mt-6">
                   <h5 className="font-semibold text-gray-700 mb-3">
                     {currentLang === 'ru' ? 'Потенциальные проблемы' : 'Potential Issues'}
                   </h5>
                   
                   <div className="space-y-2">
                     {/* Критические проблемы */}
                     {seoAnalysis.headings.h1.length === 0 && (
                       <div className="space-y-2">
                         <div className="flex items-center text-red-600 text-sm">
                           <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                             <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                           </svg>
                           {currentLang === 'ru' ? 'Отсутствует H1 заголовок' : 'Missing H1 heading'}
                         </div>
                         
                         {/* Пример добавления H1 */}
                         <div className="ml-6 bg-red-50 p-3 rounded border border-red-200">
                           <div className="text-xs font-medium text-red-800 mb-2">
                             {currentLang === 'ru' ? 'Добавьте в <body>:' : 'Add to <body>:'}
                           </div>
                           <div className="bg-gray-900 text-green-400 p-2 rounded text-xs font-mono overflow-x-auto">
                             {`<h1>Основной заголовок страницы</h1>`}
                           </div>
                           <div className="mt-2 text-xs text-red-700">
                             <div><strong>Рекомендации:</strong></div>
                             <div>• Используйте ключевые слова в заголовке</div>
                             <div>• Длина 50-60 символов</div>
                             <div>• Только один H1 на странице</div>
                           </div>
                         </div>
                       </div>
                     )}
                     
                     {!seoAnalysis.metaTags.description && (
                       <div className="space-y-2">
                         <div className="flex items-center text-red-600 text-sm">
                           <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                             <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                           </svg>
                           {currentLang === 'ru' ? 'Отсутствует meta description' : 'Missing meta description'}
                         </div>
                         
                         {/* Пример добавления meta description */}
                         <div className="ml-6 bg-red-50 p-3 rounded border border-red-200">
                           <div className="text-xs font-medium text-red-800 mb-2">
                             {currentLang === 'ru' ? 'Добавьте в <head>:' : 'Add to <head>:'}
                           </div>
                           <div className="bg-gray-900 text-green-400 p-2 rounded text-xs font-mono overflow-x-auto flex justify-between items-start">
                             <span className="flex-1">
                               {`<meta name="description" content="Краткое описание страницы (150-160 символов)" />`}
                             </span>
                             <button
                               onClick={() => copyToClipboard(`<meta name="description" content="Краткое описание страницы (150-160 символов)" />`)}
                               className="ml-2 text-blue-400 hover:text-blue-300 text-xs"
                               title={currentLang === 'ru' ? 'Копировать' : 'Copy'}
                             >
                               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                               </svg>
                             </button>
                           </div>
                         </div>
                       </div>
                     )}
                     
                     {!seoAnalysis.canonical.href && (
                       <div className="space-y-2">
                         <div className="flex items-center text-red-600 text-sm">
                           <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                             <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                           </svg>
                           {currentLang === 'ru' ? 'Отсутствует canonical URL' : 'Missing canonical URL'}
                         </div>
                         
                         {/* Пример добавления canonical */}
                         <div className="ml-6 bg-red-50 p-3 rounded border border-red-200">
                           <div className="text-xs font-medium text-red-800 mb-2">
                             {currentLang === 'ru' ? 'Добавьте в <head>:' : 'Add to <head>:'}
                           </div>
                           <div className="bg-gray-900 text-green-400 p-2 rounded text-xs font-mono overflow-x-auto">
                             {`<link rel="canonical" href="${page?.url || 'https://example.com/page-url'}" />`}
                           </div>
                         </div>
                       </div>
                     )}
                     
                     {seoAnalysis.content.wordCount < 300 && (
                       <div className="flex items-center text-red-600 text-sm">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                         </svg>
                         {currentLang === 'ru' ? `Мало контента (${seoAnalysis.content.wordCount} слов, рекомендуется минимум 300)` : `Low content (${seoAnalysis.content.wordCount} words, recommended minimum 300)`}
                       </div>
                     )}
                     
                     {/* Предупреждения */}
                     {seoAnalysis.headings.h1.length > 1 && (
                       <div className="space-y-2">
                         <div className="flex items-center text-yellow-600 text-sm">
                           <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                             <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                           </svg>
                           {currentLang === 'ru' ? 'Множественные H1 заголовки' : 'Multiple H1 headings'}
                         </div>
                         
                         {/* Детали H1 заголовков */}
                         <div className="ml-6 space-y-2">
                           {seoAnalysis.headings.h1.map((h1, index) => (
                             <div key={index} className="bg-yellow-50 p-3 rounded border border-yellow-200">
                               <div className="text-xs font-medium text-yellow-800 mb-2">
                                 {currentLang === 'ru' ? 'H1' : 'H1'} #{index + 1}:
                               </div>
                               <div className="bg-gray-900 text-green-400 p-2 rounded text-xs font-mono overflow-x-auto flex justify-between items-start">
                                 <span className="flex-1">
                                   {`<h1>${h1}</h1>`}
                                 </span>
                                 <button
                                   onClick={() => copyToClipboard(`<h1>${h1}</h1>`)}
                                   className="ml-2 text-blue-400 hover:text-blue-300 text-xs"
                                   title={currentLang === 'ru' ? 'Копировать' : 'Copy'}
                                 >
                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                   </svg>
                                 </button>
                               </div>
                               <div className="mt-2 text-xs text-yellow-700">
                                 <div><strong>текст:</strong> {h1}</div>
                               </div>
                             </div>
                           ))}
                         </div>
                       </div>
                     )}
                     
                     {seoAnalysis.images.withoutAlt > 0 && (
                       <div className="space-y-2">
                         <div className="flex items-center text-yellow-600 text-sm">
                           <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                             <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                           </svg>
                           {currentLang === 'ru' ? `${seoAnalysis.images.withoutAlt} изображений без alt атрибута` : `${seoAnalysis.images.withoutAlt} images without alt attribute`}
                         </div>
                         
                         {/* Детали проблемных изображений */}
                         <div className="ml-6 space-y-2">
                           {seoAnalysis.images.list
                             .filter(img => !img.alt || !img.alt.trim())
                             .map((img, index) => (
                               <div key={index} className="bg-yellow-50 p-3 rounded border border-yellow-200">
                                 <div className="text-xs font-medium text-yellow-800 mb-2">
                                   {currentLang === 'ru' ? 'Изображение' : 'Image'} #{index + 1}:
                                 </div>
                                                            <div className="bg-gray-900 text-green-400 p-2 rounded text-xs font-mono overflow-x-auto flex justify-between items-start">
                             <span className="flex-1">
                               {`<img src="${img.src}"${img.title ? ` title="${img.title}"` : ''}${img.width ? ` width="${img.width}"` : ''}${img.height ? ` height="${img.height}"` : ''} />`}
                             </span>
                             <button
                               onClick={() => copyToClipboard(`<img src="${img.src}"${img.title ? ` title="${img.title}"` : ''}${img.width ? ` width="${img.width}"` : ''}${img.height ? ` height="${img.height}"` : ''} alt="описание изображения" />`)}
                               className="ml-2 text-blue-400 hover:text-blue-300 text-xs"
                               title={currentLang === 'ru' ? 'Копировать с alt' : 'Copy with alt'}
                             >
                               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                               </svg>
                             </button>
                           </div>
                                 <div className="mt-2 text-xs text-yellow-700">
                                   <div><strong>src:</strong> {img.src}</div>
                                   {img.title && <div><strong>title:</strong> {img.title}</div>}
                                   {img.width && img.height && (
                                     <div><strong>размеры:</strong> {img.width}×{img.height}</div>
                                   )}
                                 </div>
                               </div>
                             ))}
                         </div>
                       </div>
                     )}
                     
                     {!seoAnalysis.openGraph.title && (
                       <div className="space-y-2">
                         <div className="flex items-center text-yellow-600 text-sm">
                           <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                             <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                           </svg>
                           {currentLang === 'ru' ? 'Отсутствует Open Graph title' : 'Missing Open Graph title'}
                         </div>
                         
                         {/* Пример добавления Open Graph title */}
                         <div className="ml-6 bg-yellow-50 p-3 rounded border border-yellow-200">
                           <div className="text-xs font-medium text-yellow-800 mb-2">
                             {currentLang === 'ru' ? 'Добавьте в <head>:' : 'Add to <head>:'}
                           </div>
                           <div className="bg-gray-900 text-green-400 p-2 rounded text-xs font-mono overflow-x-auto">
                             {`<meta property="og:title" content="Заголовок для социальных сетей" />`}
                           </div>
                         </div>
                       </div>
                     )}
                     
                     {!seoAnalysis.openGraph.description && (
                       <div className="flex items-center text-yellow-600 text-sm">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                         </svg>
                         {currentLang === 'ru' ? 'Отсутствует Open Graph description' : 'Missing Open Graph description'}
                       </div>
                     )}
                     
                     {!seoAnalysis.openGraph.image && (
                       <div className="flex items-center text-yellow-600 text-sm">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                         </svg>
                         {currentLang === 'ru' ? 'Отсутствует Open Graph image' : 'Missing Open Graph image'}
                       </div>
                     )}
                     
                     {!seoAnalysis.additionalMeta.language && (
                       <div className="space-y-2">
                         <div className="flex items-center text-yellow-600 text-sm">
                           <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                             <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                           </svg>
                           {currentLang === 'ru' ? 'Не указан язык страницы' : 'Page language not specified'}
                         </div>
                         
                         {/* Пример добавления языка */}
                         <div className="ml-6 bg-yellow-50 p-3 rounded border border-yellow-200">
                           <div className="text-xs font-medium text-yellow-800 mb-2">
                             {currentLang === 'ru' ? 'Добавьте в <html>:' : 'Add to <html>:'}
                           </div>
                           <div className="bg-gray-900 text-green-400 p-2 rounded text-xs font-mono overflow-x-auto">
                             {`<html lang="ru">`}
                           </div>
                           <div className="mt-2 text-xs text-yellow-700">
                             <div><strong>Варианты:</strong></div>
                             <div>• <code>lang="ru"</code> - для русского</div>
                             <div>• <code>lang="en"</code> - для английского</div>
                             <div>• <code>lang="uk"</code> - для украинского</div>
                           </div>
                         </div>
                       </div>
                     )}
                     
                     {seoAnalysis.content.textAnalysis && seoAnalysis.content.textAnalysis.densityLevel === 'high' && (
                       <div className="flex items-center text-yellow-600 text-sm">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                         </svg>
                         {currentLang === 'ru' ? `Высокая тошнота текста (${seoAnalysis.content.textAnalysis.density}%)` : `High text density (${seoAnalysis.content.textAnalysis.density}%)`}
                       </div>
                     )}
                     
                     {seoAnalysis.links.total === 0 && (
                       <div className="flex items-center text-yellow-600 text-sm">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                         </svg>
                         {currentLang === 'ru' ? 'Отсутствуют ссылки на странице' : 'No links on the page'}
                       </div>
                     )}
                     
                     {seoAnalysis.forms.total === 0 && seoAnalysis.content.wordCount > 500 && (
                       <div className="flex items-center text-blue-600 text-sm">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                         </svg>
                         {currentLang === 'ru' ? 'Рекомендуется добавить форму обратной связи' : 'Consider adding a contact form'}
                       </div>
                     )}
                     
                     {/* Проблемы с ключевыми запросами */}
                     {keywordAnalysis && keywordAnalysis.some(result => result.status === 'missing') && (
                       <div className="flex items-center text-red-600 text-sm">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                         </svg>
                         {currentLang === 'ru' ? 'Некоторые ключевые запросы не найдены в контенте' : 'Some keywords not found in content'}
                       </div>
                     )}
                     
                     {keywordAnalysis && keywordAnalysis.some(result => result.status === 'words') && (
                       <div className="flex items-center text-yellow-600 text-sm">
                         <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                           <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                         </svg>
                         {currentLang === 'ru' ? 'Ключевые запросы найдены только частично' : 'Keywords found only partially'}
                       </div>
                     )}
                   </div>
                 </div>
              </div>
            )}
            {/* URL и статус */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0">
                <div className="flex-1 w-full">
                  <h4 className="text-sm font-medium text-gray-600 mb-2">
                    {currentLang === 'ru' ? 'URL:' : 'URL:'}
                  </h4>
                  <a 
                    href={page.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-base sm:text-lg font-semibold text-blue-600 hover:text-blue-800 break-all flex items-start gap-2"
                    title={currentLang === 'ru' ? 'Открыть в новой вкладке' : 'Open in new tab'}
                  >
                    <span className="break-all">{page.url}</span>
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                  <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-bold rounded-full ${
                    page.h_status?.includes('200') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {page.h_status || 'N/A'}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-500">
                    {formatDate(page.check_date)}
                  </span>
                </div>
              </div>
            </div>

            {/* Основная SEO информация */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">
                    {currentLang === 'ru' ? 'Title:' : 'Title:'}
                  </h4>
                  <p className="text-gray-800 bg-gray-50 p-3 rounded-lg text-sm break-words">
                    {decodeBase64(page.title)}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">
                    {currentLang === 'ru' ? 'Description:' : 'Description:'}
                  </h4>
                  <p className="text-gray-800 bg-gray-50 p-3 rounded-lg text-sm break-words">
                    {decodeBase64(page.description)}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">
                    {currentLang === 'ru' ? 'Keywords:' : 'Keywords:'}
                  </h4>
                  <p className="text-gray-800 bg-gray-50 p-3 rounded-lg text-sm break-words">
                    {decodeBase64(page.keywords)}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">
                    {currentLang === 'ru' ? 'Canonical:' : 'Canonical:'}
                  </h4>
                  <p className="text-gray-800 bg-gray-50 p-3 rounded-lg text-sm break-all">
                    {decodeBase64(page.canonical) || (currentLang === 'ru' ? 'Не указан' : 'Not specified')}
                  </p>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">
                    {currentLang === 'ru' ? 'H1:' : 'H1:'}
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    {headers.h1 && headers.h1.length > 0 ? (
                      <div className="space-y-1">
                        {headers.h1.map((h1, index) => (
                          <div key={index} className="text-sm text-gray-800 break-words">
                            {h1}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">
                        {currentLang === 'ru' ? 'Не найдено' : 'Not found'}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">
                    {currentLang === 'ru' ? 'H2:' : 'H2:'}
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    {headers.h2 && headers.h2.length > 0 ? (
                      <div className="space-y-1">
                        {headers.h2.map((h2, index) => (
                          <div key={index} className="text-sm text-gray-800 break-words">
                            {h2}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">
                        {currentLang === 'ru' ? 'Не найдено' : 'Not found'}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">
                    {currentLang === 'ru' ? 'H3:' : 'H3:'}
                  </h4>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    {headers.h3 && headers.h3.length > 0 ? (
                      <div className="space-y-1">
                        {headers.h3.map((h3, index) => (
                          <div key={index} className="text-sm text-gray-800 break-words">
                            {h3}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">
                        {currentLang === 'ru' ? 'Не найдено' : 'Not found'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>


            {/* Информация о загрузке */}
            {(() => {
              const textTdData = parseTextTd(page.text_td);
              if (textTdData && textTdData.text) {
                return (
                  <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border-l-4 border-blue-500">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">
                      {currentLang === 'ru' ? 'Информация о загрузке:' : 'Load Information:'}
                    </h4>
                    <div className="text-sm text-blue-700 whitespace-pre-line break-words">
                      {textTdData.text}
                    </div>
                    {textTdData.headers && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <h5 className="text-xs font-medium text-blue-800 mb-2">
                          {currentLang === 'ru' ? 'HTTP заголовки:' : 'HTTP Headers:'}
                        </h5>
                        <div className="grid grid-cols-1 gap-2 text-xs">
                          {Object.entries(textTdData.headers).map(([key, value]) => (
                            <div key={key} className="flex flex-col sm:flex-row sm:justify-between">
                              <span className="font-medium text-blue-700">{key}:</span>
                              <span className="text-blue-600 break-all">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })()}

            {/* Статистика */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              <div className="text-center">
                <div className="text-base sm:text-lg font-bold text-gray-800">{page.linkIn}</div>
                <div className="text-xs text-gray-600">
                  {currentLang === 'ru' ? 'Внутренние ссылки' : 'Internal Links'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-lg font-bold text-gray-800">{page.linkOut}</div>
                <div className="text-xs text-gray-600">
                  {currentLang === 'ru' ? 'Внешние ссылки' : 'External Links'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-lg font-bold text-gray-800">{page.weight}</div>
                <div className="text-xs text-gray-600">
                  {currentLang === 'ru' ? 'Вес' : 'Weight'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-lg font-bold text-gray-800">
                  {textTdData?.size ? `${(textTdData.size / 1024).toFixed(1)} KB` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600">
                  {currentLang === 'ru' ? 'Размер' : 'Size'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-lg font-bold text-gray-800">
                  {textTdData && textTdData.down_time ? `${textTdData.down_time.toFixed(0)}ms` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600">
                  {currentLang === 'ru' ? 'Время загрузки' : 'Load Time'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-base sm:text-lg font-bold text-gray-800">
                  {textTdData && textTdData.speed ? `${textTdData.speed.toFixed(1)}ms` : 'N/A'}
                </div>
                <div className="text-xs text-gray-600">
                  {currentLang === 'ru' ? 'Скорость' : 'Speed'}
                </div>
              </div>
            </div>

            {/* Внутренние ссылки */}
            {(() => {
              const internalLinks = parseLinks(page.linkInText);
              if (internalLinks.length > 0) {
                return (
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">
                      {currentLang === 'ru' ? 'Внутренние ссылки:' : 'Internal Links:'} ({internalLinks.length})
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg max-h-60 overflow-y-auto">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 text-xs sm:text-sm">
                                {currentLang === 'ru' ? 'Тип' : 'Type'}
                              </th>
                              <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 text-xs sm:text-sm">
                                {currentLang === 'ru' ? 'Анкор' : 'Anchor'}
                              </th>
                              <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 text-xs sm:text-sm">
                                {currentLang === 'ru' ? 'URL' : 'URL'}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {internalLinks.map((link, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-2 sm:px-3 py-2">
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                                    link.type === 'Follow' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {link.type}
                                  </span>
                                </td>
                                <td className="px-2 sm:px-3 py-2">
                                  <span className="text-gray-800 font-medium text-xs sm:text-sm break-words">{link.anchor}</span>
                                </td>
                                <td className="px-2 sm:px-3 py-2">
                                  <span className="text-gray-600 break-all text-xs">{link.url}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Внешние ссылки */}
            {(() => {
              const externalLinks = parseLinks(page.linkOutText);
              if (externalLinks.length > 0) {
                return (
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">
                      {currentLang === 'ru' ? 'Внешние ссылки:' : 'External Links:'} ({externalLinks.length})
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg max-h-60 overflow-y-auto">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 text-xs sm:text-sm">
                                {currentLang === 'ru' ? 'Тип' : 'Type'}
                              </th>
                              <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 text-xs sm:text-sm">
                                {currentLang === 'ru' ? 'Анкор' : 'Anchor'}
                              </th>
                              <th className="px-2 sm:px-3 py-2 text-left font-medium text-gray-700 text-xs sm:text-sm">
                                {currentLang === 'ru' ? 'URL' : 'URL'}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {externalLinks.map((link, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-2 sm:px-3 py-2">
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                                    link.type === 'Follow' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {link.type}
                                  </span>
                                </td>
                                <td className="px-2 sm:px-3 py-2">
                                  <span className="text-gray-800 font-medium text-xs sm:text-sm break-words">{link.anchor}</span>
                                </td>
                                <td className="px-2 sm:px-3 py-2">
                                  <span className="text-gray-600 break-all text-xs">{link.url}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            
          </div>
        </div>
      </div>

      {/* Модалка редактирования */}
      <EditPageModal
        page={page}
        siteId={siteId}
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onPageUpdated={() => {
          if (onPageUpdated) {
            onPageUpdated();
          }
          if (onReopenModal) {
            onReopenModal();
          }
        }}
      />

      {/* Модалка истории изменений */}
      <PageHistoryModal
        pageId={pageId}
        siteId={siteId}
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
      />
    </div>
  );
};

export default PageDetailModal; 